<template>
  <div class="system-monitor">
    <el-row :gutter="16" class="monitor-row">
      <!-- 活跃用户 + 今日访问日志 -->
      <el-col :xs="24" :sm="24" :md="16">
        <el-card shadow="hover" class="monitor-card">
          <template #header>
            <div class="card-header">
              <span class="card-title">
                <el-icon class="title-icon"><User /></el-icon>
                活跃用户
              </span>
              <el-button type="primary" plain size="small" :loading="loading" @click="loadActiveUsers">
                <el-icon><Refresh /></el-icon>
                <span style="margin-left: 4px">刷新</span>
              </el-button>
            </div>
          </template>

          <!-- 统计 -->
          <el-row :gutter="12" class="stat-row">
            <el-col :span="8">
              <div class="stat-item stat-item--primary">
                <div class="stat-value">{{ activeUserCount }}</div>
                <div class="stat-label">当前活跃用户</div>
              </div>
            </el-col>
            <el-col :span="8">
              <div class="stat-item stat-item--danger">
                <div class="stat-value">{{ errorCount }}</div>
                <div class="stat-label">错误次数</div>
              </div>
            </el-col>
            <el-col :span="8">
              <div class="stat-item stat-item--success">
                <div class="stat-value">{{ activeUsers.length }}</div>
                <div class="stat-label">用户条目</div>
              </div>
            </el-col>
          </el-row>

          <!-- 活跃用户表格 -->
          <div class="section-title">
            <el-icon><UserFilled /></el-icon>
            <span style="margin-left: 4px">用户列表</span>
          </div>
          <el-table
            :data="activeUsers"
            v-loading="loading"
            border
            stripe
            size="default"
            class="user-table"
          >
            <el-table-column type="index" label="序号" width="70" align="center" />
            <el-table-column prop="user" label="用户名" min-width="120" show-overflow-tooltip />
            <el-table-column prop="count" label="访问次数" min-width="100" align="center">
              <template #default="{ row }">
                <el-tag type="primary" size="small">{{ row.count ?? 0 }}</el-tag>
              </template>
            </el-table-column>
          </el-table>

          <!-- 今日访问日志 -->
          <div class="section-title">
            <el-icon><Document /></el-icon>
            <span style="margin-left: 4px">今日访问日志</span>
            <el-tag
              v-if="todayLogs.length || logLoading"
              size="small"
              type="info"
              effect="plain"
              style="margin-left: 8px"
            >
              共 {{ todayLogs.length }} 条
            </el-tag>
            <el-button
              type="primary"
              plain
              size="small"
              class="section-refresh-btn"
              :loading="logLoading"
              @click="onRefreshLogs"
            >
              <el-icon><Refresh /></el-icon>
              <span style="margin-left: 4px">刷新</span>
            </el-button>
          </div>

          <!-- 搜索条件 -->
          <el-form :inline="true" :model="logSearchForm" class="log-search-form" @submit.prevent>
            <el-form-item label="用户">
              <el-input
                v-model="logSearchForm.User"
                placeholder="用户名"
                clearable
                size="default"
                style="width: 140px"
                @keyup.enter="onSearchLogs"
              />
            </el-form-item>
            <el-form-item label="接口URL">
              <el-input
                v-model="logSearchForm.url"
                placeholder="接口路径/关键字"
                clearable
                size="default"
                style="width: 200px"
                @keyup.enter="onSearchLogs"
              />
            </el-form-item>
            <el-form-item label="时间范围">
              <el-date-picker
                v-model="logTimeRange"
                type="datetimerange"
                range-separator="~"
                start-placeholder="开始时间"
                end-placeholder="结束时间"
                format="YYYY-MM-DD HH:mm:ss"
                value-format="YYYY-MM-DD HH:mm:ss"
                :default-time="defaultTimeRange"
                style="width: 380px"
              />
            </el-form-item>
            <el-form-item>
              <el-button type="primary" :loading="logLoading" @click="onSearchLogs">
                <el-icon><Search /></el-icon>
                <span style="margin-left: 4px">搜索</span>
              </el-button>
              <el-button :disabled="logLoading" @click="onResetLogs">重置</el-button>
            </el-form-item>
          </el-form>

          <!-- 日志表格 -->
          <el-table
            :data="todayLogs"
            v-loading="logLoading"
            border
            stripe
            height="480"
            class="log-table"
          >
            <el-table-column type="index" label="#" width="55" align="center" fixed="left" />
            <el-table-column
              v-for="col in logColumns"
              :key="`${col.prop || col.label}-${logColumns.length}`"
              :prop="col.prop"
              :label="col.label"
              :width="col.width"
              :min-width="col.minWidth"
              :align="col.align || 'left'"
              :fixed="col.fixed"
              :show-overflow-tooltip="col.type !== 'json' && col.type !== 'reqres' && col.showOverflowTooltip !== false"
            >
              <!-- JSON 内容列：格式化 + 点击弹窗查看 -->
              <template v-if="col.type === 'json'" #default="{ row }">
                <div class="json-cell" @click="openJsonViewer(row[col.prop!], col.label)">
                  {{ formatJsonText(row[col.prop!]) }}
                </div>
              </template>
              <!-- 请求/响应标记列 -->
              <template v-else-if="col.type === 'reqres'" #default="{ row }">
                <el-tag
                  v-if="row.HasRequest"
                  size="small"
                  type="success"
                  effect="plain"
                >Req</el-tag>
                <el-tag
                  v-if="row.HasResponse"
                  size="small"
                  type="primary"
                  effect="plain"
                  style="margin-left: 4px"
                >Res</el-tag>
                <span v-if="!row.HasRequest && !row.HasResponse">-</span>
              </template>
            </el-table-column>
          </el-table>
        </el-card>
      </el-col>

      <!-- 系统运行状态 -->
      <el-col :xs="24" :sm="24" :md="8">
        <el-card shadow="hover" class="monitor-card status-card">
          <template #header>
            <div class="card-header">
              <span class="card-title">
                <el-icon class="title-icon"><Monitor /></el-icon>
                系统运行状态
              </span>
              <el-button type="primary" plain size="small" :loading="statusLoading" @click="loadStatus">
                <el-icon><Refresh /></el-icon>
                <span style="margin-left: 4px">刷新</span>
              </el-button>
            </div>
          </template>

          <el-descriptions :column="1" border class="status-descriptions">
            <el-descriptions-item
              v-for="field in statusFields"
              :key="field.key"
              :label="field.label"
              label-align="right"
              label-width="110px"
              align="left"
            >
              <span class="status-value">{{ status[field.key] ?? '-' }}</span>
            </el-descriptions-item>
          </el-descriptions>
        </el-card>
      </el-col>
    </el-row>

    <!-- JSON 内容查看弹窗 -->
    <el-dialog
      v-model="jsonViewerVisible"
      :title="jsonViewerTitle"
      width="70%"
      top="5vh"
      append-to-body
    >
      <div class="json-viewer-body">
        <pre class="json-viewer-pre">{{ jsonViewerContent }}</pre>
      </div>
      <template #footer>
        <el-button type="primary" @click="copyJsonViewer">
          <el-icon><CopyDocument /></el-icon>
          <span style="margin-left: 4px">复制内容</span>
        </el-button>
        <el-button @click="jsonViewerVisible = false">关闭</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script lang="ts" setup>
import { onMounted, reactive, ref } from 'vue'
import { ElMessage } from 'element-plus'
import {
  CopyDocument,
  Document,
  Monitor,
  Refresh,
  Search,
  User,
  UserFilled,
} from '@element-plus/icons-vue'
import SystemMonitorApi, { TodayAccessLogItem } from '@/network/apis/SystemMonitor'

interface ActiveUser {
  user?: string
  count?: number
}

const statusFields = [
  { label: '环境名称', key: 'EnvironmentName' },
  { label: '系统架构', key: 'OSArchitecture' },
  { label: '内容根路径', key: 'ContentRootPath' },
  { label: 'Web根路径', key: 'WebRootPath' },
  { label: '框架描述', key: 'FrameworkDescription' },
  { label: '内存占用', key: 'MemoryFootprint' },
  { label: '运行时长', key: 'WorkingTime' },
]

const loading = ref(false)
const statusLoading = ref(false)
const logLoading = ref(false)

const activeUsers = ref<ActiveUser[]>([])
const activeUserCount = ref(0)
const errorCount = ref(0)
const status = ref<Record<string, any>>({})

// 今日访问日志
const todayLogs = ref<TodayAccessLogItem[]>([])

// 搜索表单（输入框不填就不传）
const logSearchForm = reactive<{
  User: string
  url: string
  start: string
  end: string
}>({
  User: '',
  url: '',
  start: '',
  end: '',
})

// ========== 日志表格动态列 ==========
interface LogColumn {
  prop?: string
  label: string
  width?: number
  minWidth?: number
  align?: 'left' | 'center' | 'right'
  fixed?: boolean | 'left' | 'right'
  type?: 'text' | 'json' | 'reqres'
  showOverflowTooltip?: boolean
}

// 字段名 → 中文表头（仅用于翻译标签，不是列白名单：
// 后端数据里有这个字段就显示，没有就不显示，未知字段直接用字段名）
const FIELD_LABELS: Record<string, string> = {
  Time: '请求时间',
  User: '用户',
  IP: 'IP',
  Path: '接口',
  QueryString: 'Query参数',
  BodyData: '请求体',
  ResponseData: '响应数据',
  TraceId: 'TraceId',
  HasRequest: '请求/响应',
  HasResponse: '请求/响应',
}

// 根据后端返回的数据字段自动生成表头（后端返回什么字段，表头就显示什么）
const buildColumnsFromData = (items: any[]): LogColumn[] => {
  if (!Array.isArray(items) || items.length === 0) return []
  const keys = new Set<string>()
  items.forEach((item) => {
    if (item && typeof item === 'object') {
      Object.keys(item).forEach((k) => keys.add(k))
    }
  })
  const cols: LogColumn[] = []
  keys.forEach((key) => {
    const label = FIELD_LABELS[key] ?? key
    if (key === 'BodyData' || key === 'ResponseData') {
      // JSON 内容列：格式化 + 点击弹窗
      cols.push({
        prop: key,
        label,
        minWidth: key === 'ResponseData' ? 300 : 260,
        type: 'json',
      })
    } else if (key === 'HasRequest' || key === 'HasResponse') {
      // 请求/响应标记合并为一列（HasResponse 不重复生成）
      if (key === 'HasRequest') {
        cols.push({ prop: key, label, minWidth: 100, align: 'center', type: 'reqres' })
      }
    } else {
      cols.push({
        prop: key,
        label,
        minWidth: key === 'Time' ? 160 : 130,
        align: key === 'Time' ? 'center' : 'left',
        fixed: key === 'Time' ? 'left' : undefined,
        showOverflowTooltip: true,
      })
    }
  })
  return cols
}

const logColumns = ref<LogColumn[]>([])

// 从接口响应解析后端返回的列定义（后端显式返回 columns 时优先使用）
const parseColumns = (res: any): LogColumn[] | null => {
  const rawCols = res?.columns ?? res?.response?.columns ?? null
  if (!Array.isArray(rawCols) || rawCols.length === 0) return null
  const cols = rawCols
    .map((c: any) => ({
      prop: c.prop ?? c.field ?? c.key,
      label: c.label ?? c.title ?? c.name ?? '',
      width: c.width,
      minWidth: c.minWidth ?? c.min_width,
      align: c.align,
      fixed: c.fixed,
      type: c.type,
      showOverflowTooltip: c.showOverflowTooltip,
    }))
    .filter((c: LogColumn) => c.prop && c.label)
  return cols.length > 0 ? cols : null
}

// 时间范围选择器：[开始时间, 结束时间]
const logTimeRange = ref<[string, string] | null>(null)

// datetimerange 的 default-time 必须是 Date 对象数组，不能传字符串数组
const defaultTimeRange: [Date, Date] = [
  new Date(0, 0, 0, 0, 0, 0),
  new Date(0, 0, 0, 23, 59, 59),
]

// 构造请求参数：去掉空字符串字段
const buildLogParams = () => {
  // 将时间范围选择器的值同步到表单字段
  if (logTimeRange.value && logTimeRange.value.length === 2) {
    logSearchForm.start = logTimeRange.value[0] || ''
    logSearchForm.end = logTimeRange.value[1] || ''
  } else {
    logSearchForm.start = ''
    logSearchForm.end = ''
  }
  const params: Record<string, string> = {}
  for (const [k, v] of Object.entries(logSearchForm)) {
    const value = (v ?? '').trim()
    if (value) params[k] = value
  }
  return params
}

const loadLogs = async (params: Record<string, string> = {}) => {
  logLoading.value = true
  try {
    const res: any = await SystemMonitorApi.getTodayAccessLogDetails(params)
    const data = res?.response
    todayLogs.value = Array.isArray(data) ? data : []
    // 表头生成优先级：后端显式返回 columns > 从返回数据字段自动生成（有数据时）
    const cols = parseColumns(res)
    if (cols) {
      logColumns.value = cols
    } else if (todayLogs.value.length > 0) {
      logColumns.value = buildColumnsFromData(todayLogs.value)
    } else {
      logColumns.value = []
    }
  } catch (err) {
    console.error('获取今日访问日志失败:', err)
    todayLogs.value = []
  } finally {
    logLoading.value = false
  }
}

const onSearchLogs = () => {
  loadLogs(buildLogParams())
}

const onResetLogs = () => {
  logSearchForm.User = ''
  logSearchForm.url = ''
  logSearchForm.start = ''
  logSearchForm.end = ''
  logTimeRange.value = null
  todayLogs.value = []
  logColumns.value = []
}

// 刷新：保留当前筛选条件重新加载最新日志
const onRefreshLogs = () => {
  loadLogs(buildLogParams())
}

// ========== JSON 内容格式化展示 ==========
const jsonViewerVisible = ref(false)
const jsonViewerTitle = ref('')
const jsonViewerContent = ref('')

// 尝试将字符串按 JSON 格式化，解析失败则原样返回（如 "[文件响应]..."、"Status Code: 404" 等）
const formatJsonText = (value: any): string => {
  if (value == null || value === '') return ''
  const raw = String(value)
  try {
    return JSON.stringify(JSON.parse(raw), null, 2)
  } catch {
    return raw
  }
}

const openJsonViewer = (value: any, title: string) => {
  jsonViewerContent.value = formatJsonText(value)
  jsonViewerTitle.value = title
  jsonViewerVisible.value = true
}

const copyJsonViewer = async () => {
  try {
    await navigator.clipboard.writeText(jsonViewerContent.value)
    ElMessage.success('已复制到剪贴板')
  } catch {
    ElMessage.error('复制失败，请手动选择复制')
  }
}

const loadActiveUsers = async () => {
  loading.value = true
  try {
    const res: any = await SystemMonitorApi.getActiveUsers()
    const data = res?.response || {}
    activeUsers.value = Array.isArray(data.activeUsers) ? data.activeUsers : []
    activeUserCount.value = data.activeUserCount ?? activeUsers.value.length
    errorCount.value = data.errorCount ?? 0
  } catch (err) {
    console.error('获取活跃用户失败:', err)
  } finally {
    loading.value = false
  }
}

const loadStatus = async () => {
  statusLoading.value = true
  try {
    const res: any = await SystemMonitorApi.getSystemStatus()
    status.value = res?.response || {}
  } catch (err) {
    console.error('获取系统运行状态失败:', err)
  } finally {
    statusLoading.value = false
  }
}

const loadAll = async () => {
  await Promise.all([loadActiveUsers(), loadStatus()])
}

onMounted(loadAll)
</script>

<style scoped>
.system-monitor {
  padding: 4px;
}

.monitor-row {
  display: flex;
  flex-wrap: wrap;
}

.monitor-row :deep(.el-col) {
  display: flex;
}

.monitor-card {
  margin-bottom: 16px;
  width: 100%;
  display: flex;
  flex-direction: column;
}

.monitor-card :deep(.el-card__body) {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.card-title {
  display: inline-flex;
  align-items: center;
  font-size: 16px;
  font-weight: 600;
  color: var(--el-text-color-primary);
}

.title-icon {
  margin-right: 6px;
  color: var(--el-color-primary);
  font-size: 18px;
}

.stat-row {
  margin-bottom: 18px;
}

.stat-item {
  width: 100%;
  text-align: center;
  padding: 20px 0;
  border-radius: 10px;
  background: var(--el-fill-color-light);
  border: 1px solid var(--el-border-color-lighter);
  transition: all 0.25s;
}

.stat-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
}

.stat-item--primary {
  background: linear-gradient(135deg, #ecf5ff 0%, #d9ecff 100%);
}

.stat-item--danger {
  background: linear-gradient(135deg, #fef0f0 0%, #fde2e2 100%);
}

.stat-item--success {
  background: linear-gradient(135deg, #f0f9eb 0%, #dbf3e1 100%);
}

.stat-item--primary .stat-value {
  color: var(--el-color-primary);
}

.stat-item--danger .stat-value {
  color: var(--el-color-danger);
}

.stat-item--success .stat-value {
  color: var(--el-color-success);
}

.stat-value {
  font-size: 30px;
  font-weight: 700;
  line-height: 1.2;
}

.stat-label {
  margin-top: 4px;
  font-size: 13px;
  color: var(--el-text-color-secondary);
}

.section-title {
  display: flex;
  align-items: center;
  margin: 8px 0 10px;
  font-size: 14px;
  font-weight: 600;
  color: var(--el-text-color-regular);
}

.section-title .el-icon {
  color: var(--el-color-primary);
}

.section-title .section-refresh-btn {
  margin-left: auto;
}

.user-table {
  margin-bottom: 18px;
}

.log-search-form {
  display: flex;
  flex-wrap: wrap;
  gap: 0;
  margin-bottom: 12px;
  padding: 12px;
  background: var(--el-fill-color-light);
  border-radius: 8px;
  border: 1px solid var(--el-border-color-lighter);
}

.log-search-form :deep(.el-form-item) {
  margin-bottom: 0;
  margin-right: 12px;
}

.log-table :deep(.el-table__cell) {
  white-space: nowrap;
}

.status-card {
  position: sticky;
  top: 16px;
}

.status-descriptions {
  border-radius: 8px;
  overflow: hidden;
}

.status-value {
  word-break: break-all;
  color: var(--el-text-color-regular);
  font-family: 'Consolas', 'Monaco', monospace;
  font-size: 13px;
}

/* JSON 单元格展示 */
.json-cell {
  font-family: 'Consolas', 'Monaco', monospace;
  font-size: 12px;
  line-height: 1.5;
  color: var(--el-text-color-regular);
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 140px;
  overflow: hidden;
  cursor: pointer;
  transition: color 0.2s;
}

.json-cell:hover {
  color: var(--el-color-primary);
}

/* JSON 查看弹窗 */
.json-viewer-body {
  max-height: 65vh;
  overflow: auto;
  background: #0f172a;
  border-radius: 8px;
  padding: 16px;
}

.json-viewer-pre {
  margin: 0;
  color: #e2e8f0;
  font-family: 'Consolas', 'Monaco', monospace;
  font-size: 13px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-all;
}
</style>
